// What stands between signing in and the application: `/api/me`, and what it
// says the person may do.
//
// ANYBODY IN THE TENANT CAN SIGN IN, and most of them have no access. The API
// puts them on the roster the first time `/api/me` answers them, says
// `"access": "none"`, and refuses them everything else. So until it has
// answered nothing of the application is drawn -- no rail, no menu, no routes --
// and for somebody with no access nothing of it ever is. That is also what keeps
// an address typed straight into the bar from reaching a screen: there are no
// routes to match it. The address itself is left alone, so once an Admin has
// granted access, Check again or a reload goes where the person was heading.
//
// Hiding the application is a courtesy rather than the control. The API refuses
// a person with no access on every endpoint but `/api/me`.

import type { ReactNode } from 'react';
import { signOut } from '../auth';
import type { Async } from '../data';
import type { Me } from '../types';
import { ErrorNote } from './Screen';

export const NO_ACCESS_MESSAGE =
  'Welcome to Barrier Tracker.  You are signed in but have no access.  Please speak to a Barrier Tracker admin to receive access.';

export function AccessGate({ me, children }: { me: Async<Me>; children: ReactNode }) {
  // A failed `/api/me` means nothing else would work either, so it is the whole
  // page rather than a note above screens that would each fail the same way.
  if (me.error) {
    return (
      <main className="boot-signin">
        <h1>Barrier Tracker</h1>
        <ErrorNote error={me.error} />
        <div className="boot-actions">
          <button className="primary" disabled={me.loading} onClick={me.reload}>
            Try again
          </button>
          <button className="link" onClick={() => void signOut()}>
            Sign out
          </button>
        </div>
      </main>
    );
  }

  // Not answered yet. Neither the application nor the landing page, so that
  // nobody sees one flash up before the other.
  if (!me.data) {
    return (
      <main className="boot-signin">
        <p className="muted">Signing in…</p>
      </main>
    );
  }

  if (me.data.access === 'none') return <NoAccessScreen me={me.data} checking={me.loading} onCheck={me.reload} />;

  return <>{children}</>;
}

function NoAccessScreen({ me, checking, onCheck }: { me: Me; checking: boolean; onCheck: () => void }) {
  return (
    <main className="boot-signin">
      <h1>Barrier Tracker</h1>
      <p className="muted">
        {me.display_name ? `${me.display_name} · ${me.actor}` : me.actor}
      </p>
      <p>{NO_ACCESS_MESSAGE}</p>
      <div className="boot-actions">
        <button className="primary" disabled={checking} onClick={onCheck}>
          {checking ? 'Checking…' : 'Check again'}
        </button>
        <button className="link" onClick={() => void signOut()}>
          Sign out
        </button>
      </div>
    </main>
  );
}
